/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadLexendFont } from 'modules/dashboard/components/tab-container/workbook-report/components/widget-pane/style';
import {
  ALERT_NAME_MAP,
  PDF_BODY_SIZE,
  PDF_BOTTOM_LIMIT,
  PDF_HEADING_COLOR,
  PDF_HEADING_SIZES,
  PDF_LINE_RATIO,
  PDF_LINK_COLOR,
  PDF_MARGIN_X,
  PDF_TABLE_HEAD_FILL,
  PDF_TABLE_LINE_COLOR,
  PDF_TABLE_SIZE,
  PDF_TEXT_COLOR,
  PDF_TEXT_WIDTH,
  PDF_TOP_Y,
  semanticAnalysisPickupConfig,
  semanticAnalysisTrendConfig,
} from './constants';

export const formatLabel = (label) => {
  if (!label) return '';

  if (label === 'stlp' || label === 'Stlp' || label === 'STLP') {
    return 'STLP';
  }

  if (label === 'topOrBottomStaydates') {
    return 'Top or Bottom Target Dates';
  }

  if (label === 'nStaydates') {
    return 'No. of Target Dates';
  }

  if (label === 'pmsSyncOff' || label === 'pms_sync') {
    return 'PMS Sync';
  }

  if (label === 'USD' || label === 'RC' || label === 'ADR') {
    return label; // leave as-is
  }

  // Insert space before uppercase letters
  const spaced = label.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Lowercase everything first, then uppercase first letter of each word
  return spaced
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatAlertName = (name) =>
  ALERT_NAME_MAP[name] ??
  name
    .replace(/_vs_stlp|_alert/g, '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const convertHeading = (line) => line.replace(/^\*\*(.+?)\*\*$/, '## $1');

const convertBullet = (line) => line.replace(/^(\s*)\*\s+/, '$1- ');

export const convertToMarkdown = (input = '') =>
  String(input ?? '')
    .replaceAll('\r', '')
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();

      // BE: **Heading**
      if (/^\*\*(.+?)\*\*$/.test(trimmed)) {
        return convertHeading(trimmed);
      }

      // BE: * List item (supports nesting)
      if (/^\s*\*\s+/.test(line)) {
        return convertBullet(line);
      }

      return line;
    })
    .join('\n')
    // collapse excessive blank lines
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const isSemanticType = (type) =>
  type === semanticAnalysisTrendConfig.typeName || type === semanticAnalysisPickupConfig.typeName;

// Consumes backslash escapes of ASCII punctuation, matching CommonMark
// (e.g. "\*" renders as a literal "*" in the drawer).
const unescapeMarkdown = (text) => text.replace(/\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g, '$1');

// Matches the inline constructs the drawer's ReactMarkdown renders:
// **bold**/__bold__, ![alt](src), [label](url), `code`, *em*/_em_, ~~strike~~.
// Emphasis content cannot start/end with whitespace, underscore emphasis is
// not recognized intraword, and escaped delimiters are ignored — matching
// CommonMark flanking rules ("5 * 3 * 2", "RevPAR_Index", "\*x\*" all stay
// literal, as they do in the drawer). Link URLs may contain one balanced
// parenthesis level, as in "…/f(1)".
const INLINE_TOKEN =
  /(?<!\\)\*\*(\S(?:.*?\S)?)(?<!\\)\*\*|(?<![\w\\])__(\S(?:.*?\S)?)(?<!\\)__(?!\w)|(?<!\\)!\[([^\]]*)]\(((?:[^()\s]|\([^()\s]*\))*)\)|(?<!\\)\[([^\]]+)]\(((?:[^()\s]|\([^()\s]*\))*)\)|(?<!\\)`([^`]+)`|(?<!\\)\*(\S[^*]*?\S|\S)(?<!\\)\*|(?<![\w\\])_(\S[^_]*?\S|\S)(?<!\\)_(?!\w)|(?<!\\)~~(\S(?:.*?\S)?)(?<!\\)~~/g;

// Splits a line into styled runs for the PDF writer. Bold and code runs keep
// their emphasis; links become clickable blue text; italic and strikethrough
// flatten to plain text because only regular/bold Lexend faces are embedded.
export const parseInlineRuns = (text) => {
  const source = String(text);
  const runs = [];
  let cursor = 0;
  [...source.matchAll(INLINE_TOKEN)].forEach((match) => {
    if (match.index > cursor) {
      runs.push({ text: unescapeMarkdown(source.slice(cursor, match.index)) });
    }
    if (match[1] != null || match[2] != null) {
      runs.push({ text: unescapeMarkdown(match[1] ?? match[2]), bold: true });
    } else if (match[3] != null) {
      // remote images can't be embedded; the alt text stands in for the <img>
      runs.push({ text: unescapeMarkdown(match[3]) });
    } else if (match[5] != null) {
      runs.push({ text: unescapeMarkdown(match[5]), link: match[6] });
    } else if (match[7] != null) {
      runs.push({ text: match[7], code: true }); // code spans render literally
    } else {
      runs.push({ text: unescapeMarkdown(match[8] ?? match[9] ?? match[10]) });
    }
    cursor = match.index + match[0].length;
  });
  if (cursor < source.length) runs.push({ text: unescapeMarkdown(source.slice(cursor)) });
  return runs.filter((run) => run.text !== '');
};

// Plain-text form of inline markdown — used for table cells, where run
// styles cannot be mixed.
const inlinePlainText = (text) =>
  parseInlineRuns(text)
    .map((run) => run.text)
    .join('');

// Only http(s) links get PDF annotations; the drawer's ReactMarkdown drops
// unsafe hrefs via its default urlTransform, so unsafe links degrade to
// plain text here as well.
const SAFE_LINK_PATTERN = /^https?:\/\//i;

// Advances the cursor to the next line, adding a page when past the bottom
// margin.
const pdfNewLine = (doc, state, lineHeight) => {
  state.y += lineHeight;
  if (state.y + lineHeight > PDF_BOTTOM_LIMIT) {
    doc.addPage();
    state.y = PDF_TOP_Y;
  }
};

// Writes styled runs with word wrap and page breaks; `state.y` is the baseline
// cursor and ends one line below the last printed line. `marker` (e.g. "•" or
// "1.") is drawn hanging at `indent` so wrapped lines align under the text,
// matching the drawer's list rendering.
const writePdfRuns = (
  doc,
  state,
  runs,
  { size = PDF_BODY_SIZE, bold = false, indent = 0, marker = '', color = PDF_TEXT_COLOR } = {}
) => {
  const lineHeight = size * PDF_LINE_RATIO;
  const maxX = PDF_MARGIN_X + PDF_TEXT_WIDTH;
  //
  doc.setFont('lexend', 'normal');
  doc.setFontSize(size);
  const markerX = PDF_MARGIN_X + indent;
  const textX = marker ? markerX + doc.getTextWidth(`${marker} `) : markerX;
  let x = textX;
  //
  const newLine = () => {
    pdfNewLine(doc, state, lineHeight);
    x = textX;
  };
  //
  if (state.y + lineHeight > PDF_BOTTOM_LIMIT) {
    doc.addPage();
    state.y = PDF_TOP_Y;
  }
  doc.setTextColor(...color);
  if (marker) doc.text(marker, markerX, state.y);
  //
  runs.forEach((run) => {
    if (!run.text) return;
    const url = run.link && SAFE_LINK_PATTERN.test(run.link) ? run.link : null;
    doc.setFont(run.code ? 'courier' : 'lexend', bold || run.bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...(url ? PDF_LINK_COLOR : color));
    //
    const drawWord = (word) => {
      const wordWidth = doc.getTextWidth(word);
      // x > textX means the line already holds content (words or whitespace);
      // a word at an empty line always fits — anything wider takes the split path
      if (x > textX && x + wordWidth > maxX) newLine();
      if (url) {
        doc.textWithLink(word, x, state.y, { url });
        doc.setDrawColor(...PDF_LINK_COLOR);
        doc.line(x, state.y + 0.5, x + wordWidth, state.y + 0.5);
      } else {
        doc.text(word, x, state.y);
      }
      x += wordWidth;
    };
    //
    run.text.split(/(\s+)/).forEach((word) => {
      if (!word) return;
      if (/^\s+$/.test(word)) {
        // whitespace tokens keep their width (a tab ≈ 4 spaces) so leading
        // indentation survives, matching the drawer's whiteSpace handling;
        // if the indent itself reaches the margin, the next word wraps
        x += doc.getTextWidth(word.replaceAll('\t', '    '));
        return;
      }
      // Rare overlong tokens (e.g. URLs) are split mid-word so they cannot
      // overflow; each piece keeps the run's styling and link annotation.
      if (doc.getTextWidth(word) > maxX - textX) {
        doc.splitTextToSize(word, maxX - textX).forEach(drawWord);
        return;
      }
      drawWord(word);
    });
  });
  state.y += lineHeight;
};

const writePdfLines = (
  doc,
  state,
  text,
  { style = 'normal', size = PDF_BODY_SIZE, indent = 0, color = PDF_TEXT_COLOR } = {}
) =>
  writePdfRuns(doc, state, [{ text: String(text ?? '') }], {
    size,
    bold: style === 'bold',
    indent,
    color,
  });

const parsePipeRow = (line) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/(?<!\\)\|$/, '')
    .split(/(?<!\\)\|/) // escaped pipes ("\|") stay inside their cell, like GFM
    .map((cell) => inlinePlainText(cell.trim()));

const isSeparatorRow = (cells) => cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell));

// Writes a semantic markdown report into the document, mirroring the drawer's
// ReactMarkdown styling: heading sizes differ per level, bullets/ordered items
// hang-indent like ul/ol, pipe tables become real PDF tables, and inline
// **bold**, [links](url) and `code` keep their emphasis. `state.y` tracks the
// cursor.
const writeMarkdownToPdf = (doc, state, markdown) => {
  const lines = String(markdown).split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = line.trim();
    //
    if (!trimmed) {
      state.y += 4; // blank line ≈ the 12px paddingTop on each UI paragraph
    } else if (trimmed.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i += 1;
      }
      i -= 1;
      // GFM only treats the block as a table when a delimiter row follows the
      // header; otherwise the drawer renders the pipes as literal text.
      if (!(tableLines.length > 1 && isSeparatorRow(parsePipeRow(tableLines[1])))) {
        tableLines.forEach((l) => writePdfRuns(doc, state, parseInlineRuns(l.trim())));
      } else {
        const rows = tableLines.map(parsePipeRow).filter((cells) => !isSeparatorRow(cells));
        const [head, ...body] = rows;
        // start on a fresh page when the header row alone would not fit
        if (state.y + 3 + PDF_TABLE_SIZE * PDF_LINE_RATIO > PDF_BOTTOM_LIMIT) {
          doc.addPage();
          state.y = PDF_TOP_Y;
        }
        autoTable(doc, {
          head: head ? [head] : undefined,
          body,
          startY: state.y + 3,
          margin: { left: PDF_MARGIN_X, right: PDF_MARGIN_X },
          theme: 'grid',
          styles: {
            font: 'lexend',
            fontSize: PDF_TABLE_SIZE,
            cellPadding: { vertical: 2.8, horizontal: 4 }, // UI: 8px 12px
            textColor: PDF_TEXT_COLOR,
            lineColor: PDF_TABLE_LINE_COLOR,
            lineWidth: 0.2,
          },
          headStyles: {
            fillColor: PDF_TABLE_HEAD_FILL,
            textColor: PDF_HEADING_COLOR,
            fontStyle: 'bold',
          },
        });
        state.y = doc.lastAutoTable.finalY + 4;
      }
    } else {
      const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
      const rule = trimmed.match(/^([-*_])\1{2,}$/);
      const bullet = line.match(/^(\s*)-\s+(.*)$/);
      const ordered = line.match(/^(\s*)(\d{1,3}[.)])\s+(.*)$/);
      if (heading) {
        const level = heading[1].length;
        const headingSize = PDF_HEADING_SIZES[level] ?? PDF_BODY_SIZE;
        // keep the heading on the same page as the first line of its content
        if (state.y + 7 + (headingSize + PDF_BODY_SIZE) * PDF_LINE_RATIO > PDF_BOTTOM_LIMIT) {
          doc.addPage();
          state.y = PDF_TOP_Y;
        }
        state.y += level <= 2 ? 7 : 5;
        writePdfRuns(doc, state, parseInlineRuns(heading[2]), {
          bold: true,
          size: headingSize,
          color: PDF_HEADING_COLOR,
        });
        state.y += 2.5;
      } else if (rule) {
        // a rule at the page bottom moves to a fresh page rather than drawing
        // below the margin
        if (state.y + 6 > PDF_BOTTOM_LIMIT) {
          doc.addPage();
          state.y = PDF_TOP_Y;
        }
        state.y += 2;
        doc.setDrawColor(...PDF_TABLE_LINE_COLOR);
        doc.line(PDF_MARGIN_X, state.y, PDF_MARGIN_X + PDF_TEXT_WIDTH, state.y);
        state.y += 4;
      } else if (bullet) {
        writePdfRuns(doc, state, parseInlineRuns(bullet[2]), {
          // UI ul: 20px padding per level; capped so deep nesting can't overflow
          indent: 2 + Math.min(Math.floor(bullet[1].length / 2), 8) * 7,
          marker: '\u2022',
        });
        state.y += 1.4; // UI li marginBottom 4px
      } else if (ordered) {
        writePdfRuns(doc, state, parseInlineRuns(ordered[3]), {
          indent: 2 + Math.min(Math.floor(ordered[1].length / 2), 8) * 7,
          marker: ordered[2],
        });
        state.y += 1.4;
      } else {
        writePdfRuns(doc, state, parseInlineRuns(trimmed));
      }
    }
  }
};

/**
 * Downloads a semantic alert report as a PDF. The report body is the same
 * markdown produced by convertToMarkdown for the log drawer; pipe tables are
 * rendered as tables and the methodology block is appended when present. A
 * "Report Parameters" section is rendered before the methodology when
 * parameter pairs are provided; it carries the evaluation input (hotel,
 * target dates, batch) for users and for debugging.
 * @param {string} title - Report title (notification type).
 * @param {{ result?: string, methodology?: string }} remarks - Semantic remarks payload.
 * @param {Array<[string, string]>} parameters - [label, value] pairs for the parameters section.
 * @param {string} fileName - Downloaded file name without extension.
 */
export const exportSemanticLogToPdf = ({ title, remarks, fileName } = {}) => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF();
  loadLexendFont(doc);
  const state = { y: PDF_TOP_Y };
  //
  // UI: Typography h4 (18px ≈ 13.5pt) in text.secondary
  writePdfLines(doc, state, title, { style: 'bold', size: 13.5, color: PDF_HEADING_COLOR });
  state.y += 4;
  //
  writeMarkdownToPdf(doc, state, convertToMarkdown(remarks?.result));
  //
  if (remarks?.methodology) {
    // keep the section heading with its first line
    if (state.y + 10 + (12 + PDF_BODY_SIZE) * PDF_LINE_RATIO > PDF_BOTTOM_LIMIT) {
      doc.addPage();
      state.y = PDF_TOP_Y;
    }
    state.y += 10;
    // UI: Typography h6 (16px ≈ 12pt)
    writePdfLines(doc, state, 'Methodology', { style: 'bold', size: 12, color: PDF_HEADING_COLOR });
    state.y += 2;
    // UI renders methodology as plain pre-line text, so no markdown parsing
    // here; the backend only emits literal "<br>" separators
    String(remarks.methodology)
      .replaceAll('<br>', '\n')
      .split('\n')
      .forEach((line) => writePdfRuns(doc, state, [{ text: line }]));
  }
  //
  const safeName =
    String(fileName ?? '')
      .trim()
      .replace(/[/\\:*?"<>|]/g, '-') || 'semantic-report';
  doc.save(`${safeName}.pdf`);
};

/**
 * Derives the display frequency for an alert configuration:
 * - numeric evalLead entries → schedule is driven by targetDateRecurrence
 * - string evalLead entries → day(s) of week or "everyday", each entry
 *   capitalized ("MONDAY" → "Monday") and joined with ", "
 *
 * TODO (revisit): the configuration table calls this with only the latest alert of
 * each grouped row, assuming every alert in the group shares the same schedule
 * configuration; revisit if that assumption breaks (see alert-table.jsx).
 */
export const getAlertFrequency = (alert) => {
  const evalLeads = Array.isArray(alert?.evalLead)
    ? alert.evalLead
    : [alert?.evalLead].filter((lead) => lead != null);
  const dayOfWeekLeads = evalLeads
    .filter((lead) => typeof lead === 'string')
    // Capitalize each entry: first letter capital, rest lowercase (e.g. "MONDAY" → "Monday")
    .map((lead) => lead.charAt(0).toUpperCase() + lead.slice(1).toLowerCase());
  return dayOfWeekLeads.length
    ? dayOfWeekLeads.join(', ')
    : formatLabel(alert?.targetDateRecurrence);
};

/**
 * HACK (temporary): strips the alert's own hotel-code prefix from a semantic alert
 * name so report variants that share one alertProc (e.g. trend "This Month" vs
 * "Next Month") can be grouped/toggled separately in the configuration table.
 * Only prefixes that exactly match one of the alert's own hotelIds are stripped;
 * the prefix length is derived from the matched code, never assumed fixed-length.
 * This mirrors normalizeSemanticAlertName in amica-backend-app
 * (src/services/alerts/alerts-util.js) and MUST stay in sync with it so the
 * configuration rows match the emails the backend groups per report variant.
 * Expected to be refactored once semantic alerts get a proper reportType field or
 * a distinct alertProc per report variant.
 */
export const normalizeSemanticAlertName = (name, hotelIds) => {
  const trimmed = String(name || '').trim();
  const codes = (Array.isArray(hotelIds) ? hotelIds : [hotelIds]).filter(Boolean);

  const matchingCode = codes.find(
    (code) => trimmed.startsWith(`${code} `) || trimmed.startsWith(`${code}-`)
  );

  return matchingCode !== undefined
    ? trimmed.slice(String(matchingCode).length + 1).trim()
    : trimmed;
};
